import React, { useMemo, useState } from 'react';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import ReplyOutlined from '@mui/icons-material/ReplyOutlined';
import { useHistory } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import {
  isReportResponse,
  ManagerReport,
  selectManagerReports,
  setReportingNavItem,
} from 'pages/Reporting/redux';
import { selectUserEmail } from 'pages/Hospitality/redux';
import { HOSPITALITY_PATHS } from 'pages/Layout/hospitalityModulePaths';
import { usePath } from 'pages/hooks';
import LayoutAlert from './LayoutAlert';
import LayoutAlertPresence from './LayoutAlertPresence';
import { responseAccent } from './ManagerReportViewer.styled';
import { logButtonClick } from 'utils/analytics';

const DISMISS_STORAGE_KEY = 'manager_reports_alert_dismissed_v1';
const DISMISS_MS = 60 * 60 * 1000; // 1 hour
const reportAccent = '#1d4ed8';

type ManagerReportsAlertDismissState = {
  until: number,
  /** Inbox ids present when dismissed — any newer id brings the alert back. */
  knownIds: string[],
};

const emptyDismissState = (): ManagerReportsAlertDismissState => ({
  until: 0,
  knownIds: [],
});

const readDismissState = (): ManagerReportsAlertDismissState => {
  try {
    const raw = localStorage.getItem(DISMISS_STORAGE_KEY);
    if (!raw) return emptyDismissState();
    const parsed = JSON.parse(raw);
    const knownIds = Array.isArray(parsed?.knownIds)
      ? parsed.knownIds.map((id: unknown) => String(id)).filter(Boolean)
      : [];
    return {
      until: Number(parsed?.until) || 0,
      knownIds,
    };
  } catch {
    return emptyDismissState();
  }
};

const writeDismissState = (state: ManagerReportsAlertDismissState) => {
  try {
    localStorage.setItem(DISMISS_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota / private mode */
  }
};

export const filterManagerReportsForUser = (
  reports: ManagerReport[] | null | undefined,
  userEmail: string | null | undefined,
): ManagerReport[] => {
  const email = String(userEmail || '').trim().toLowerCase();
  if (!email || !reports?.length) return [];
  return reports.filter((r) => {
    if (String(r.toEmail || '').toLowerCase() !== email) return false;
    if (r.isOutgoing) return false;
    // Filed / dismissed items stay in the Reporting pane for 7 days — they should not keep the alert up.
    if (r.reviewedAt) return false;
    return true;
  });
};

/** True while the current inbox is snoozed (no new ids since dismiss). */
export const isManagerReportsAlertSnoozed = (
  reportIds: string[],
  dismissState: ManagerReportsAlertDismissState = readDismissState(),
): boolean => {
  if (!reportIds.length) return false;
  if (Date.now() >= dismissState.until) return false;
  const known = new Set(dismissState.knownIds);
  // Any id not known at dismiss time → new item → show again.
  return reportIds.every((id) => known.has(id));
};

/**
 * Inbox strip for reports + responses — higher priority than storage (MB) alerts.
 * Shown to whoever the item was sent to, only while unreviewed items exist and not snoozed.
 */
export const isManagerReportsAlertActive = (
  reports: ManagerReport[] | null | undefined,
  isHomePath: boolean,
  userEmail?: string | null,
): boolean => {
  if (isHomePath) return false;
  const mine = filterManagerReportsForUser(reports, userEmail);
  if (!mine.length) return false;
  const ids = mine.map((r) => String(r.id));
  return !isManagerReportsAlertSnoozed(ids);
};

const ManagerReportsAlert = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const reports = useAppSelector(selectManagerReports);
  const userEmail = useAppSelector(selectUserEmail);
  const { isHomePath } = usePath();
  const [dismissState, setDismissState] = useState(readDismissState);

  const myReports = useMemo(
    () => filterManagerReportsForUser(reports, userEmail),
    [reports, userEmail],
  );

  const count = myReports.length;
  const latest = myReports[0];
  const latestIsResponse = isReportResponse(latest);
  const reportIds = useMemo(() => myReports.map((r) => String(r.id)), [myReports]);
  const snoozed = isManagerReportsAlertSnoozed(reportIds, dismissState);
  const active = !isHomePath && count > 0 && !snoozed;

  const onDismiss = () => {
    const next: ManagerReportsAlertDismissState = {
      until: Date.now() + DISMISS_MS,
      knownIds: reportIds,
    };
    writeDismissState(next);
    setDismissState(next);
    logButtonClick('dismiss_manager_reports_alert');
  };

  const open = active && !!latest;
  const responseCount = myReports.filter((r) => isReportResponse(r)).length;
  const reportCount = count - responseCount;
  let title = '';
  if (reportCount > 0 && responseCount > 0) {
    title = `You have ${count} report items`;
  } else if (latestIsResponse) {
    title = count === 1
      ? 'You have 1 report response'
      : `You have ${count} report responses`;
  } else {
    title = count === 1
      ? 'You have 1 new staff report'
      : `You have ${count} staff reports`;
  }

  return (
    <LayoutAlertPresence open={open}>
      {latest ? (
        <LayoutAlert
          belowOverlays
          paleStrip
          accent={latestIsResponse ? responseAccent : reportAccent}
          icon={latestIsResponse
            ? <ReplyOutlined fontSize="small" />
            : <DescriptionOutlined fontSize="small" />}
          title={title}
          text={(
            <>
              Latest:
              {' '}
              {latestIsResponse ? 'Response — ' : ''}
              {latest.subject || latest.preview || latest.description || 'Open to view'}
            </>
          )}
          textEllipsis
          secondaryAction={{ label: 'Dismiss', onClick: onDismiss }}
          primaryAction={{
            label: latestIsResponse ? 'View response' : 'View report',
            onClick: () => {
              logButtonClick(
                latestIsResponse
                  ? 'view_manager_report_response_alert'
                  : 'view_manager_report_alert',
              );
              dispatch(setReportingNavItem(String(latest.id)));
              history.push(HOSPITALITY_PATHS.reportingTab(String(latest.id)));
            },
          }}
        />
      ) : null}
    </LayoutAlertPresence>
  );
};

export default ManagerReportsAlert;
